import { Modal } from "@circleco/compass/components/Modal";
import type { ReactNode } from "react";
import { useLayoutEffect, useState } from "react";

const scrollTopFadeClass =
  "pointer-events-none absolute top-0 left-0 right-0 z-10 h-10 transition-opacity duration-200 motion-reduce:transition-none";
const customLayoutBodyClass =
  "text-primary flex min-h-0 flex-1 flex-col overflow-y-auto rounded-b-lg text-body-md";

const getModalBodyScroller = (testId: string) => {
  const root = document.querySelector(`[data-testid="${testId}"]`);
  return root?.querySelector(".overflow-y-auto") ?? null;
};

const ScrollTopFade = ({ targetTestId }: { targetTestId: string }) => {
  const [shouldShowTopFade, setShouldShowTopFade] = useState(false);

  useLayoutEffect(() => {
    const scroller = getModalBodyScroller(targetTestId);
    if (!scroller) return;

    const syncFade = () => {
      const { scrollTop, scrollHeight, clientHeight } = scroller;
      setShouldShowTopFade(scrollHeight > clientHeight && scrollTop > 1);
    };

    syncFade();
    scroller.addEventListener("scroll", syncFade);
    const resizeObserver = new ResizeObserver(syncFade);
    resizeObserver.observe(scroller);

    return () => {
      scroller.removeEventListener("scroll", syncFade);
      resizeObserver.disconnect();
    };
  }, [targetTestId]);

  return (
    <div
      aria-hidden
      className={scrollTopFadeClass}
      style={{
        background:
          "linear-gradient(180deg, var(--color-background-primary) 0%, transparent 72%)",
        opacity: shouldShowTopFade ? 1 : 0,
      }}
    />
  );
};

interface QuickPostModalBodyProps {
  children: ReactNode;
  customLayout?: boolean;
  "data-testid": string;
}

export const QuickPostModalBody = ({
  children,
  customLayout = false,
  "data-testid": dataTestId,
}: QuickPostModalBodyProps) => {
  const body = customLayout ? (
    <div className={customLayoutBodyClass} data-testid={dataTestId}>
      {children}
    </div>
  ) : (
    <Modal.Body data-testid={dataTestId}>{children}</Modal.Body>
  );

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
      <ScrollTopFade targetTestId={dataTestId} />
      {body}
    </div>
  );
};
